import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SankeyChart,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { useGetOverviewReviewsExplanationsSankey } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useSankeyChartData from 'screens/performanceReview/manageAndInsights/ReviewManage/components/SankeyChart/hooks/dataHooks';
import REACT_QUERY_KEYS_REVIEWS_INSIGHTS from 'screens/performanceReview/manageAndInsights/ReviewManage/components/TrendLineChart/constants/reactQueryKeysReviewsInsights';
import { useGetAnalyticsFiltersDataQuery } from 'screens/performanceReview/manageAndInsights/ReviewManage/dataHooks';
import { checkSandboxStaticDataFeaturesEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

const mockData = {
  nodes: [
    {
      uuid: '22',
      name: 'High performer',
      group: 'Annual Performance Review 2023',
      displayOrder: 2,
    },
    {
      uuid: '19',
      name: 'Risk',
      group: 'Annual Performance Review 2024',
      displayOrder: 9,
    },
    {
      uuid: '27',
      name: 'Inconsistent player',
      group: 'Annual Performance Review 2023',
      displayOrder: 7,
    },
    {
      uuid: '13',
      name: 'High Potential',
      group: 'Annual Performance Review 2024',
      displayOrder: 3,
    },
    {
      uuid: '21',
      name: 'Star',
      group: 'Annual Performance Review 2023',
      displayOrder: 1,
    },
    {
      uuid: '14',
      name: 'Core player',
      group: 'Annual Performance Review 2024',
      displayOrder: 4,
    },
    {
      uuid: '12',
      name: 'High performer',
      group: 'Annual Performance Review 2024',
      displayOrder: 2,
    },
    {
      uuid: '11',
      name: 'Star',
      group: 'Annual Performance Review 2024',
      displayOrder: 1,
    },
    {
      uuid: '23',
      name: 'High Potential',
      group: 'Annual Performance Review 2023',
      displayOrder: 3,
    },
  ],
  nodeData: [
    {
      fromNode: '12',
      toNode: '22',
      weight: 1,
    },
    {
      fromNode: '11',
      toNode: '21',
      weight: 1,
    },
    {
      fromNode: '13',
      toNode: '23',
      weight: 1,
    },
    {
      fromNode: '14',
      toNode: '22',
      weight: 1,
    },
    {
      fromNode: '19',
      toNode: '21',
      weight: 1,
    },
    {
      fromNode: '11',
      toNode: '27',
      weight: 1,
    },
  ],
  srcReviewCycle: null,
};

const mockReviewCycle = {
  id: 989,
  keyName: 'Mid Year Review 2023',

  displayName: 'Mid Year Review 2023',
  uuid: '1000000003503',

  enabled: true,
  performanceEvaluationGrid: null,
  reviewType: 'STAR_RATED',
  feedbackForms: [
    {
      uuid: '1000000001593',
      keyName: 'All Employees',
      displayName: 'All Employees',
      enabled: true,
    },
  ],
};
const PlotSankeyChart = forwardRef(({ chartData }, ref) => {
  const { nodeData, nodes } = chartData;

  const groups = useMemo(() => {
    const fromNodeGroup = nodes?.find((node) => node.uuid === nodeData[0]?.fromNode);
    const toNodeGroup = nodes?.find((node) => node.uuid === nodeData[0]?.toNode);

    return [
      {
        id: 0,
        name: fromNodeGroup?.group,
      },
      {
        id: 1,
        name: toNodeGroup?.group,
      },
    ];
  }, [nodes, nodeData]);

  const mappedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.uuid,
        name: node.name,
        group: groups.find((group) => group.name === node.group)?.id,
      })),
    [nodes, groups]
  );

  const links = useMemo(
    () =>
      nodeData.map((link) => ({
        source: link.fromNode,
        target: link.toNode,
        weight: link.weight,
      })),
    [nodeData]
  );

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <SankeyChart
        ref={ref}
        tooltipTitle="Average Progress"
        nodes={mappedNodes}
        groups={groups}
        links={links}
        weightLabel="People"
        style={{ position: 'relative', zIndex: 1 }}
      />
    </div>
  );
});

const RenderContent = ({ selectedReviewCycleForSankeyChart, data, chartRef }) => {
  const hasData = data?.nodes?.length > 0;
  const isEmptyCycle =
    checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
      ? false
      : !selectedReviewCycleForSankeyChart;

  if (isEmptyCycle) {
    return (
      <EmptyScreen
        illustrationSrc={noInsights}
        heading="Select Review Cycle"
        description="There is no review cycle selected. Please select a review cycle to view your performance growth curve here."
      />
    );
  }

  if (hasData) {
    return (
      <Box mt="10px" minH={0} h="full" minW={0} w="full" flex={1}>
        <PlotSankeyChart chartData={data} ref={chartRef} />
      </Box>
    );
  }

  return (
    <EmptyScreen
      illustrationSrc={noInsights}
      heading="Change Review Cycle"
      description="There is no data for the selected review cycle. Please select another review cycle."
    />
  );
};

export const CompanyReviewsOverview = () => {
  const history = useHistory();
  const chartRef = useRef();
  const [selectedReviewCycleForSankeyChart, setSelectedReviewCycleForSankeyChart] = useState(null);
  const { data: analyticsFiltersData } = useGetAnalyticsFiltersDataQuery();

  const reviewCyclesList = useMemo(() => analyticsFiltersData?.source ?? [], [analyticsFiltersData?.source]);

  const [requests, setRequests] = useState({
    request1: {
      cohortId: -1,
      groupByKeys: { uuid: null, keyName: 'employeeId', displayName: 'Individual', enabled: true },
      source: null,
    },
    request2: null,
  });

  const dispatch = useDispatch();
  const [correlationId] = useState(window.crypto.randomUUID());

  const currentURL = history.location.pathname;
  const queryClient = useQueryClient();
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myCompanyReviewsOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'reviewsOverview',
    section: 'myCompany',
  });

  const { data: sankeyChartData, isLoading, isFetched } = useSankeyChartData(requests);

  useEffect(() => {
    if (sankeyChartData && sankeyChartData?.srcReviewCycle) {
      const preSelectedReviewCycleForSankeyChart = reviewCyclesList?.find(
        (value) => value.displayName === sankeyChartData?.srcReviewCycle?.name
      );
      if (!selectedReviewCycleForSankeyChart) {
        setSelectedReviewCycleForSankeyChart(preSelectedReviewCycleForSankeyChart);
      }
    }
  }, [sankeyChartData, reviewCyclesList, selectedReviewCycleForSankeyChart]);

  const handleReviewCycleChange = (value) => {
    const selectedReview = reviewCyclesList.find((review) => review.uuid === value);
    const previousRequests = { ...requests };

    // Set the new requests object with updated values
    setRequests({
      request1: {
        cohortId: -1,
        groupByKeys: { uuid: null, keyName: 'employeeId', displayName: 'Individual', enabled: true },
        source: {
          uuid: selectedReview?.uuid,
          keyName: selectedReview?.keyName,
          enabled: true,
          reviewType: selectedReview?.reviewType,
          displayName: selectedReview?.displayName,
          performanceEvaluationGrid: selectedReview?.performanceEvaluationGrid,
        },
      },
      request2: null,
    });

    setSelectedReviewCycleForSankeyChart(selectedReview);

    // Check if the new request is different from the previous one
    const isRequestChanged = !(
      isEqual(previousRequests.request1, requests.request1) && isEqual(previousRequests.request2, requests.request2)
    );

    // If the request has changed, invalidate the query
    if (isRequestChanged) {
      queryClient.invalidateQueries([REACT_QUERY_KEYS_REVIEWS_INSIGHTS.reviewsSankeyChart]);
    }
  };

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'reviewsOverview',
        section: 'myCompany',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  const { data: explanationData } = useGetOverviewReviewsExplanationsSankey({
    cohortId: -1,
    group: 'REVIEW',
    correlationId, // currently not used like other modules, sending for future use
    enabled: isFetched && sankeyChartData?.nodes?.length > 0,
    sankeyGraphResponse: sankeyChartData,
  });

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      sankeyChartData?.nodes?.length > 0 && explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData, sankeyChartData?.nodes?.length]
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  const selectValue =
    (checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
      ? mockReviewCycle
      : selectedReviewCycleForSankeyChart
    )?.uuid ?? '';

  return (
    <OverviewSection
      open={isCardOpen}
      onOpenChange={handleChange}
      title="Performance outcomes"
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation.explanation
      }
      subTitleColor={explanation.sentiment ?? 'success'}
      subTitle={
        explanation.explanation ??
        'Several employees across functions received improved ratings in their recent reviews. Praise the high achievers and discuss development plans with people leaders for underperformers.'
      }
      rightComp={
        <Select onValueChange={handleReviewCycleChange} value={selectValue}>
          <SelectTrigger>
            <SelectValue placeholder="Select review cycle" className="font-bold" />
          </SelectTrigger>
          <SelectContent style={{ maxHeight: '240px', zIndex: '1520' }}>
            {reviewCyclesList.map((option) => (
              <SelectItem value={option.uuid}>{option.displayName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Performance distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ height: '340px' }}>
            <RenderContent
              chartRef={chartRef}
              data={
                checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
                  ? mockData
                  : sankeyChartData
              }
              selectedReviewCycleForSankeyChart={selectedReviewCycleForSankeyChart}
            />
          </div>
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
